
import React, { useState, useEffect, useRef } from 'react';
import { Play, RefreshCw, Zap, Shield, Hexagon, Circle, Target } from 'lucide-react';

const FRAME_RATE = 60;
const GAME_SPEED = 1000 / FRAME_RATE;
const KILLS_PER_LEVEL = 10;

const CANNONS = {
  2: { id: 2, name: 'Dual Cannon', cooldown: 20, color: 'bg-blue-500', icon: Zap, key: '1' },
  3: { id: 3, name: 'Tri-Beam', cooldown: 30, color: 'bg-green-500', icon: Hexagon, key: '2' },
  5: { id: 5, name: 'Penta-Blast', cooldown: 45, color: 'bg-indigo-500', icon: Circle, key: '3' },
  7: { id: 7, name: 'Septa-Shield', cooldown: 60, color: 'bg-purple-500', icon: Shield, key: '4' },
  'P': { id: 'P', name: 'Prime Hunter', cooldown: 120, color: 'bg-red-600', icon: Target, key: '5' },
};

const PrimeFactorDefense = () => {
  // Game State
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameover, victory
  const [lives, setLives] = useState(10);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [cooldowns, setCooldowns] = useState({ 2: 0, 3: 0, 5: 0, 7: 0, 'P': 0 });
  const [lastFired, setLastFired] = useState(null); // For visual feedback
  const [screenShake, setScreenShake] = useState(0);

  // Entities Refs
  const enemiesRef = useRef([]);
  const projectilesRef = useRef([]); // { id, x, y, targetId, primeValue, speed }
  const particlesRef = useRef([]); // Visual effects
  const frameRef = useRef(0);
  const gameLoopRef = useRef(null);
  const baseRef = useRef(null);

  // Logic Refs (To avoid stale closures in game loop)
  const scoreRef = useRef(0);
  const levelRef = useRef(1);
  const livesRef = useRef(10);

  // Force Render
  const [, setTick] = useState(0);

  // Initialize Game
  const startGame = () => {
    setGameState('playing');
    // React State
    setLives(10);
    setLevel(1);
    setScore(0);
    // Logic Refs
    livesRef.current = 10;
    levelRef.current = 1;
    scoreRef.current = 0;
    
    setCooldowns({ 2: 0, 3: 0, 5: 0, 7: 0, 'P': 0 });
    enemiesRef.current = [];
    projectilesRef.current = [];
    particlesRef.current = [];
    frameRef.current = 0;
  };

  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = setInterval(() => {
        updateGame();
        setTick(t => t + 1);
      }, GAME_SPEED);
    } else {
      clearInterval(gameLoopRef.current);
    }
    return () => clearInterval(gameLoopRef.current);
  }, [gameState]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState !== 'playing') return;
      if (e.key === '1') fireCannon(2);
      if (e.key === '2') fireCannon(3);
      if (e.key === '3') fireCannon(5);
      if (e.key === '4') fireCannon(7);
      if (e.key === '5') fireCannon('P');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, cooldowns]);

  const updateGame = () => {
    frameRef.current++;
    if (screenShake > 0) setScreenShake(s => s - 1);
    
    // 0. Cooldowns Tick Down
    setCooldowns(prev => {
      const next = { ...prev };
      let changed = false;
      for (const key in next) {
        if (next[key] > 0) {
           next[key]--;
           changed = true;
        }
      }
      return changed ? next : prev;
    });

    // 1. Spawn Enemies (Random X Top)
    // Spawn rate also increases slightly with level
    const currentLevel = levelRef.current;
    const spawnRate = Math.max(40, 100 - (currentLevel * 10)); // Fixed rate per level
    if (frameRef.current % spawnRate === 0) { 
      spawnEnemy();
    }

    // 2. Move Enemies (Vertical Drop)
    moveEnemies();

    // 3. Move Projectiles
    moveProjectiles();

    // 4. Update Particles
    updateParticles();

    // 5. Check Game Over
    if (livesRef.current <= 0) {
      setGameState('gameover');
    }
    
    // Sync React State for UI (OPTIONAL: throttle this if performance issues, but 60FPS react state update is heavy?
    // Better to only set state on change. But for HUD valid frames:
    if (frameRef.current % 5 === 0) {
       setScore(scoreRef.current);
       setLives(livesRef.current);
       setLevel(levelRef.current);
    }
  };

  const spawnEnemy = () => {
    const lvl = levelRef.current;
    // Value range doesn't need to scale aggressively if we want just speed
    const baseValue = lvl * 5 + Math.floor(Math.random() * 20);
    let value = Math.max(4, baseValue);
    // Bias towards composites
    if (isPrime(value)) value += 1;

    const newEnemy = {
      id: Date.now() + Math.random(),
      value: value,
      maxHealth: value,
      x: 10 + Math.random() * 80, // Random X between 10% and 90%
      y: -10, // Start just above screen
      speed: 0.1 + (lvl * 0.05), // FIXED speed per level (Level 1=0.15, Level 2=0.20...)
      hitFlash: 0
    };
    enemiesRef.current.push(newEnemy);
  };

  // Helper
  const isPrime = num => {
    for(let i = 2, s = Math.sqrt(num); i <= s; i++)
        if(num % i === 0) return false; 
    return num > 1;
  }

  const moveEnemies = () => {
    enemiesRef.current.forEach((enemy, index) => {
      if (enemy.hitFlash > 0) enemy.hitFlash--;

      // Vertical Fall
      enemy.y += enemy.speed;

      // Check Breach (Bottom of screen)
      if (enemy.y > 90) { // Hit the "Cannons" line
        livesRef.current -= 1;
        setLives(livesRef.current); // Immediate UI update
        
        // Base Explosion Effect
        spawnParticles(enemy.x, 90, 'bg-red-500', 20); // Big red explosion
        setScreenShake(10);
        
        enemiesRef.current.splice(index, 1);
      }
    });
  };

  const fireCannon = (primeValue) => {
    // 1. Check Cooldown (using current state)
    if (cooldowns[primeValue] > 0) return;

    // 2. Find Target from Refs (Latest Data)
    let target = null;
    let minDist = Infinity;
    
    const originX = 50 + (String(primeValue) === 'P' ? 20 : (primeValue - 4) * 10);
    const originY = 95;

    enemiesRef.current.forEach(enemy => {
        let isTarget = false;
        // Only target visible enemies (on screen)
        if (enemy.y > 0) {
            if (primeValue === 'P') {
                    if (isPrime(enemy.value)) isTarget = true;
            } else {
                    if (enemy.value % primeValue === 0) isTarget = true;
            }
        }

        if (isTarget) {
            const dy = enemy.y - originY;
            const dx = enemy.x - originX;
            const dist = Math.sqrt(dx*dx + dy*dy);
            if (dist < minDist) {
                minDist = dist;
                target = enemy;
            }
        }
    });

    if (target) {
        // 3. FIRE (Side Effect)
        projectilesRef.current.push({
            id: Date.now() + Math.random(),
            x: originX,
            y: originY,
            targetId: target.id,
            damage: primeValue === 'P' ? 'MAX' : primeValue, 
            speed: 3,
            color: CANNONS[primeValue].color
        });
        setLastFired(primeValue);
        setTimeout(() => setLastFired(null), 100);
        
        // 4. Set Cooldown Logic
        setCooldowns(prev => ({ ...prev, [primeValue]: CANNONS[primeValue].cooldown }));
    }
  };

  const moveProjectiles = () => {
    for (let i = projectilesRef.current.length - 1; i >= 0; i--) {
      const proj = projectilesRef.current[i];
      const target = enemiesRef.current.find(e => e.id === proj.targetId);

      if (!target) {
        projectilesRef.current.splice(i, 1);
        continue;
      }

      const dx = target.x - proj.x;
      const dy = target.y - proj.y;
      const dist = Math.sqrt(dx*dx + dy*dy);

      if (dist < 3) {
        hitEnemy(target, proj.damage);
        spawnParticles(target.x, target.y, proj.color, 5);
        projectilesRef.current.splice(i, 1);
      } else {
        const vx = (dx / dist) * proj.speed;
        const vy = (dy / dist) * proj.speed;
        proj.x += vx;
        proj.y += vy;
      }
    }
  };

  const hitEnemy = (enemy, divisor) => {
    if (divisor === 'MAX') {
        if (isPrime(enemy.value)) {
           // Score update (1 point per kill)
           scoreRef.current += 1;
           checkProgression();

           enemy.value = 1;
           enemy.hitFlash = 5;
           const idx = enemiesRef.current.indexOf(enemy); 
           if (idx > -1) enemiesRef.current.splice(idx, 1);
        }
        return;
    }

    if (enemy.value % divisor === 0) {
      enemy.value = enemy.value / divisor;
      enemy.hitFlash = 5;
      
      if (enemy.value === 1) {
        // Destroyed!
        scoreRef.current += 1;
        checkProgression();
        
        const idx = enemiesRef.current.indexOf(enemy);
        if (idx > -1) enemiesRef.current.splice(idx, 1);
      }
    }
  };

  const checkProgression = () => {
      const currentScore = scoreRef.current;
      const MAX_LEVEL = 5;
      
      const calculatedLevel = Math.floor(currentScore / KILLS_PER_LEVEL) + 1;
      
      if (currentScore >= KILLS_PER_LEVEL * MAX_LEVEL) {
          setGameState('victory');
      } else if (calculatedLevel > levelRef.current) {
          levelRef.current = calculatedLevel;
          // UI Updates handled in loop sync
          setScreenShake(20); 
      }
  };

  const spawnParticles = (x, y, colorClass, count = 5) => {
      for(let i=0; i<count; i++) {
          particlesRef.current.push({
              x, y, 
              vx: (Math.random() - 0.5) * 2,
              vy: (Math.random() - 0.5) * 2,
              life: 30, // Longer life
              color: colorClass
          });
      }
  };

  const updateParticles = () => {
      for(let i = particlesRef.current.length - 1; i >= 0; i--) {
          const p = particlesRef.current[i];
          p.x += p.vx;
          p.y += p.vy;
          p.life--;
          if(p.life <= 0) particlesRef.current.splice(i, 1);
      }
  };

  return (
    <div className={`flex flex-col items-center justify-center min-h-screen bg-slate-900 text-white p-2 sm:p-4 select-none outline-none overflow-hidden transition-transform duration-75 ${screenShake > 0 ? 'translate-x-1 translate-y-1' : ''}`} tabIndex="0">
      
      {/* HUD */}
      <div className="absolute top-4 left-4 z-50">
        <button
            onClick={() => window.location.href = "/games"}
            className="flex items-center text-slate-400 hover:text-white transition-colors bg-slate-800/50 px-3 py-1 rounded-full"
        >
            <span className="mr-2">←</span> Back to Games
        </button>
      </div>
      <div className="flex w-full max-w-4xl justify-between items-end mb-4 px-4 mt-8">
         <div>
            <h1 className="text-3xl font-bold text-blue-300 leading-none">Prime Defense</h1>
            <p className="text-slate-400 text-sm">Level {level} / 5 &nbsp;|&nbsp; Target: {level * KILLS_PER_LEVEL}</p>
         </div>
         <div className="flex gap-6 text-xl font-bold">
            <div className={`flex items-center gap-2 ${lives <= 3 ? 'text-red-500 animate-pulse' : 'text-red-400'}`}>❤️ {lives}</div>
            <div className="text-purple-400 flex items-center gap-2">💀 {score}</div>
         </div>
      </div>

      {/* Main Game Area */}
      <div className="relative w-full max-w-4xl aspect-[16/9] bg-slate-800 rounded-xl overflow-hidden shadow-2xl border-2 border-slate-700 bg-grid-slate-700/50"
           ref={baseRef}>
        
        {/* Atmosphere / Background Effect - falling rain lines */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
             {[...Array(20)].map((_, i) => (
                 <div key={i} className="absolute w-[1px] bg-blue-300 h-full animate-pulse" style={{ left: `${i * 5}%`, opacity: Math.random() }} />
             ))}
        </div>

        {/* Enemies */}
        {enemiesRef.current.map((enemy) => (
          <div key={enemy.id}
               className={`absolute w-12 h-12 rounded-full flex items-center justify-center font-mono font-bold border-4 shadow-xl transform -translate-x-1/2 -translate-y-1/2 z-20 transition-all duration-75
                 ${enemy.hitFlash > 0 ? 'bg-white text-red-600 scale-125' : 'bg-red-600 text-white border-red-800'}
                 ${enemy.value > 99 ? 'text-xs' : 'text-base'}
               `}
               style={{ left: `${enemy.x}%`, top: `${enemy.y}%` }}>
             {Math.round(enemy.value)}
             {/* "Thrust" effect above enemy? No, they fall. */}
          </div>
        ))}

        {/* Projectiles */}
        {projectilesRef.current.map((proj) => (
          <div key={proj.id}
               className={`absolute w-4 h-4 ${proj.color} rounded-full z-30 transform -translate-x-1/2 -translate-y-1/2 shadow-[0_0_15px_rgba(255,255,255,0.8)]`}
               style={{ left: `${proj.x}%`, top: `${proj.y}%` }}
          />
        ))}

        {/* Particles */}
        {particlesRef.current.map((p, i) => (
            <div key={i} 
                className={`absolute w-2 h-2 rounded-sm ${p.color}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, opacity: p.life/30, transform: `scale(${p.life/10})` }}
            />
        ))}

         {/* Menu Overlay */}
         {gameState === 'menu' && (
          <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center z-50 p-8 text-center">
            <h2 className="text-5xl font-bold text-blue-400 mb-6">Prime Defense</h2>
            <div className="max-w-xl text-lg text-gray-300 mb-8 space-y-4">
                <p className="text-xl text-red-400 font-bold animate-bounce">⚠️ METEOR SHOWER DETECTED</p>
                <ul className="text-left bg-slate-800 p-6 rounded-lg space-y-2 border border-slate-600">
                    <li className="flex items-center gap-3"><Zap className="text-blue-400"/> <strong>Space Invaders style!</strong> Enemies fall from the sky.</li>
                    <li className="flex items-center gap-3"><Shield className="text-green-400"/> <strong>Defend Base:</strong> Don't let them hit the bottom!</li>
                    <li className="flex items-center gap-3"><Target className="text-red-500"/> <strong>Prime Hunter:</strong> Use RED cannon (Key 5) for stubborn Primes!</li>
                </ul>
            </div>
            <button onClick={startGame} className="px-10 py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold flex items-center gap-3 transition text-2xl shadow-lg hover:shadow-green-500/50 hover:scale-105 active:scale-95">
              <Play size={32} /> START DEFENSE
            </button>
          </div>
        )}
        
        {gameState === 'gameover' && (
          <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center z-50">
            <h2 className="text-5xl font-bold text-red-500 mb-4">BASE DESTROYED</h2>
            <p className="text-2xl text-white mb-8">Score: {score}</p>
            <button onClick={startGame} className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold flex items-center gap-2 transition">
              <RefreshCw /> Try Again
            </button>
          </div>
        )}

        {gameState === 'victory' && (
          <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center z-50">
            <h2 className="text-6xl font-bold text-yellow-400 mb-4 animate-bounce">VICTORY!</h2>
            <p className="text-2xl text-white mb-8">You defended the base!</p>
            <p className="text-xl text-purple-300 mb-8">Final Score: {score}</p>
            <button onClick={startGame} className="px-8 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold flex items-center gap-2 transition">
              <RefreshCw /> Play Again
            </button>
          </div>
        )}

      </div>

      {/* Controller Deck */}
      <div className="mt-4 grid grid-cols-5 gap-4 w-full max-w-4xl">
         {Object.values(CANNONS).map(cannon => {
           const isOnCooldown = cooldowns[cannon.id] > 0;
           return (
            <button 
                key={cannon.id}
                onClick={() => fireCannon(cannon.id)}
                className={`
                    relative h-24 rounded-xl border-b-8 transition-all active:border-b-0 active:translate-y-2 outline-none
                    flex flex-col items-center justify-center gap-1 group overflow-hidden
                    ${isOnCooldown ? 'bg-slate-700 border-slate-900 text-slate-500 cursor-wait' : `${cannon.color} border-slate-900 border-opacity-40 hover:brightness-110 active:brightness-90 text-white`}
                    ${lastFired === cannon.id ? 'ring-4 ring-white' : ''}
                `}
            >
                {/* Visual Cooldown Overlay */}
                <div 
                    className="absolute bottom-0 left-0 right-0 bg-black/30 transition-all duration-75 ease-linear"
                    style={{ height: `${(cooldowns[cannon.id] / cannon.cooldown) * 100}%` }}
                />

                <div className="text-3xl font-black z-10 flex items-center gap-2">
                    <cannon.icon size={24} /> {cannon.id}
                </div>
                <div className="text-xs uppercase font-bold tracking-wider opacity-80 z-10">
                    Key: {cannon.key}
                </div>
                
                {/* Hover Tip */}
                 <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-500 text-white text-xs p-2 rounded w-40 hidden group-hover:block z-50 text-center pointer-events-none shadow-xl">
                    {cannon.id === 'P' ? 
                      <span>Fires a <strong>Hunter Killer</strong>.<br/>Instantly destroys <strong>Prime Numbers</strong>!</span> : 
                      <span>Fires a <strong>{cannon.id}</strong> missile.<br/>Hits multiples of {cannon.id}!</span>
                    }
                 </div>
            </button>
           )
         })}
      </div>
      
      <div className="mt-4 text-slate-500 text-xs text-center">
        Tip: You can use keyboard number keys 1-5 to fire rapidly!
      </div>
    </div>
  );
};

export default PrimeFactorDefense;
